import React, { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import AccountClockIcon from "@iconify/icons-mdi/account-clock";
import AccountPlusIcon from "@iconify/icons-mdi/account-plus";
import AtIcon from "@iconify/icons-mdi/at";
import CityIcon from "@iconify/icons-mdi/city";
import CreditCardIcon from "@iconify/icons-mdi/credit-card-marker-outline";
import FileIcon from "@iconify/icons-mdi/file-account";
import FingerprintIcon from "@iconify/icons-mdi/fingerprint";
import HomeAccountIcon from "@iconify/icons-mdi/home-account";
import SiteMapIcon from "@iconify/icons-mdi/sitemap";
import { MyHrContractCardItem, MyHrPhoneCardItem } from "my-hr/components";
import { OcAvatar, OcErrorBoundary } from "oc/components";
import { useOcClientState, useOcIsMobile } from "oc/hooks";
import { getFullname } from "oc/utils";

interface MyHrContractCardProps {
  employee: any; // Replace 'any' with a proper type if available
  contract: any; // Replace 'any' with a proper type if available
}

export const MyHrContractCard: React.FC<MyHrContractCardProps> = ({
  employee,
  contract,
}) => {
  const isMobile = useOcIsMobile();
  const [textDivHeight, setTextDivHeight] = useOcClientState<number>(
    "textDivHeight",
    185,
  );
  const TextRef = React.createRef<HTMLDivElement>();

  useEffect(() => {
    let height = TextRef?.current?.clientHeight;
    if (height && textDivHeight !== height) setTextDivHeight(height);
  });

  let jobClassName =
    contract?.jobClass?.name.charAt(0).toUpperCase() +
    contract?.jobClass?.name.slice(1) || "";

  let position =
    contract?.position?.charAt(0).toUpperCase() +
    contract?.position?.slice(1) || "";

  return (
    <React.Fragment>
      <OcErrorBoundary>
        <Box padding="16px" id="my-hr-user-contract-card-data">
          <Box
            display="flex"
            flexDirection="row"
            style={{ width: "100%" }}
            justifyContent={isMobile ? "center" : "space-between"}
            alignItems="center"
          >
            <Box
              width="300px"
              marginRight={isMobile ? "unset" : "26px"}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <OcAvatar
                employee={employee?.code}
                style={{
                  width: isMobile ? "252px" : `${textDivHeight}px`,
                  height: isMobile ? "252px" : `${textDivHeight}px`,
                }}
              />
              {isMobile && (
                <>
                  <Box height="16px" />
                  <Box
                    display="flex"
                    id="employeeNameAndJob"
                    flexGrow={1}
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Box textAlign="center">
                      <Typography variant="h4">
                        {getFullname(employee)}
                      </Typography>
                      <Box height="7px" />
                      <Typography variant="h5">{jobClassName}</Typography>
                      <Typography variant="h5">{position}</Typography>
                    </Box>
                  </Box>
                </>
              )}
            </Box>
            {!isMobile && (
              <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                ref={TextRef}
                component="div"
              >
                <Box display="flex" flexDirection="row" flexGrow={1}>
                  <Box
                    display="flex"
                    id="employeeNameAndJob"
                    flexGrow={1}
                    style={{
                      borderBottom: "1px solid #5E6366",
                      paddingBottom: "9px",
                    }}
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Box textAlign="center">
                      <Typography variant="h4">
                        {getFullname(employee)}
                      </Typography>
                      <Typography variant="h5">{jobClassName}</Typography>
                      <Typography variant="h5">{position}</Typography>
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    width="300px"
                    style={{
                      borderBottom: "1px solid #5E6366",
                    }}
                  >
                    <Box>
                      {employee?.phones?.map((item: any, index: number) => {
                        return (
                          <MyHrPhoneCardItem
                            key={`${item.type} ${index}`}
                            item={item}
                          />
                        );
                      })}
                      {employee?.emails?.map((item: any, index: number) => {
                        if (item.type !== "work") return null;
                        return (
                          <MyHrContractCardItem
                            key={`${item.type} ${index}`}
                            icon={AtIcon}
                            label={item?.address}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="row" flexGrow="1">
                  <Box display="flex" flexGrow={1}>
                    <Box paddingTop="6px">
                      <MyHrContractCardItem
                        icon={CityIcon}
                        label={contract?.company?.name}
                      />
                      <MyHrContractCardItem
                        icon={HomeAccountIcon}
                        label={contract?.workingPlace?.name}
                      />
                      <MyHrContractCardItem
                        icon={SiteMapIcon}
                        label={contract?.orgUnit?.name}
                      />
                      <MyHrContractCardItem
                        icon={CreditCardIcon}
                        label={contract?.payOffice?.name}
                      />
                    </Box>
                  </Box>
                  <Box width="300px">
                    <Box paddingTop="6px">
                      <MyHrContractCardItem
                        icon={FingerprintIcon}
                        label={employee?._id}
                      />
                      <MyHrContractCardItem
                        icon={FileIcon}
                        label={contract?._id}
                      />
                      <MyHrContractCardItem
                        icon={AccountPlusIcon}
                        label={
                          contract?.startOfContract
                            ? new Date(
                              contract?.startOfContract,
                            ).toLocaleDateString()
                            : ""
                        }
                      />
                      <MyHrContractCardItem
                        icon={AccountClockIcon}
                        label={contract?.employmentType?.name}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </OcErrorBoundary>
    </React.Fragment>
  );
};

export default MyHrContractCard;
